import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Megaphone } from 'lucide-react';
import { fetchMaliNews } from '../../api/dashboard';
import type { MaliNewsItem } from '../../api/dashboard';

const SOURCE_META: Record<string, { label: string; badge: string }> = {
  resmi_gazete: { label: 'Resmî Gazete', badge: 'bg-red-50 text-red-700' },
  gib: { label: 'GİB', badge: 'bg-blue-50 text-blue-700' },
  sgk: { label: 'SGK', badge: 'bg-cyan-50 text-cyan-700' },
};

function shortDate(value: string): string {
  const datePart = value.slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}

/**
 * Dashboard "Duyuru ve Haberler" kartı: Resmî Gazete / GİB / SGK
 * duyurularından son 5 kayıt. Tamamı: /mali-haberler.
 */
export function MaliNewsCard() {
  const [news, setNews] = useState<MaliNewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMaliNews(14)
      .then((items) => setNews(items.slice(0, 5)))
      .catch(() => setNews([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm" data-testid="dashboard-news-card">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
          <Megaphone className="h-4 w-4 text-amber-500" />
          Duyuru ve Haberler
        </h3>
        <Link
          to="/mali-haberler"
          data-testid="dashboard-news-card-all"
          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
        >
          Tümü
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {news.length === 0 ? (
        <p className="py-2 text-xs text-slate-400" data-testid="dashboard-news-card-empty">
          Son 14 günde yeni duyuru yok; kaynaklar her sabah taranır.
        </p>
      ) : (
        <div className="space-y-2">
          {news.map((item) => {
            const meta = SOURCE_META[item.source] ?? { label: item.source, badge: 'bg-slate-100 text-slate-600' };
            return (
              <a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`dashboard-news-card-item-${item.id}`}
                className="block rounded-lg px-2 py-1.5 hover:bg-slate-50"
              >
                <div className="mb-0.5 flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.badge}`}>
                    {meta.label}
                  </span>
                  <span className="text-[10px] text-slate-400">{shortDate(item.published_at)}</span>
                </div>
                <div className="line-clamp-2 text-xs font-medium text-slate-700">{item.title}</div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
